import { useQuery } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import {
  CompanyCompetitorsWithThemesResponse,
  ThemeChildrenResponse,
  ThemeCompaniesResponse,
  ThemeCompetitorResponse,
  ThemeExposureResponse,
} from '@toggle/toggle';
import { WretchError } from 'wretch/types';

import {
  fetchCompanyCompetitorsWithThemes,
  fetchCompanyTheme,
  fetchCompanyThemeCompetitors,
  fetchThemeChildren,
  fetchThemeCompanies,
} from '~/services/overview-widget/overview-widget-service';
import { fetchCompetitorsCompanies } from '~/services/related-companies/related-companies-service';

import { useEntities } from '../use-entities';

export const QUERY_KEY_COMPANY_THEME = 'QUERY_KEY_COMPANY_THEME';
export const useCompanyTheme = ({
  entityTag,
  limit,
}: {
  entityTag: string;
  limit?: number;
}) =>
  useQuery<ThemeExposureResponse, WretchError>({
    queryKey: [QUERY_KEY_COMPANY_THEME, entityTag, limit],
    queryFn: ({ signal }) => fetchCompanyTheme({ entityTag, limit, signal }),
    staleTime: getTimeTillEndOfDayMs(),
  });

export const QUERY_KEY_COMPANY_THEME_COMPETITORS =
  'QUERY_KEY_COMPANY_THEME_COMPETITORS';
export const useCompanyThemeCompetitors = ({
  entityTag,
  themeId,
  limit,
}: {
  entityTag?: string;
  themeId?: string;
  limit?: number;
  enabled?: boolean;
}) =>
  useQuery<ThemeCompetitorResponse, WretchError>({
    queryKey: [QUERY_KEY_COMPANY_THEME_COMPETITORS, entityTag, themeId, limit],
    queryFn: ({ signal }) =>
      fetchCompanyThemeCompetitors({
        entityTag: entityTag!,
        themeId: themeId!,
        limit,
        signal,
      }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!entityTag && !!themeId,
  });

export const QUERY_KEY_THEME_COMPANIES = 'QUERY_KEY_THEME_COMPANIES';
export const useThemeCompanies = ({
  themeId,
  limit,
}: {
  themeId: string;
  limit?: number;
}) =>
  useQuery<ThemeCompaniesResponse, WretchError>({
    queryKey: [QUERY_KEY_THEME_COMPANIES, themeId, ...(!!limit ? [limit] : [])],
    queryFn: ({ signal }) => fetchThemeCompanies({ themeId, limit, signal }),
    staleTime: getTimeTillEndOfDayMs(),
  });

export const QUERY_KEY_THEME_SIBLINGS = 'QUERY_KEY_THEME_SIBLINGS';
export const useThemeChildren = (themeId: string, entityTag?: string) =>
  useQuery<ThemeChildrenResponse, WretchError>({
    queryKey: [QUERY_KEY_THEME_SIBLINGS, themeId],
    queryFn: ({ signal }) => fetchThemeChildren(themeId, entityTag, signal),
    staleTime: getTimeTillEndOfDayMs(),
  });

/**
 * @deprecated
 */
export const QUERY_KEY_COMPETITORS_COMPANIES =
  'QUERY_KEY_COMPETITORS_COMPANIES';
export const useCompetitorsCompanies = (
  entityTag: string,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: [QUERY_KEY_COMPETITORS_COMPANIES, entityTag],
    queryFn: () => fetchCompetitorsCompanies(entityTag),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: enabled,
  });

export const QUERY_KEY_COMPANY_COMPETITOR_WITH_THEMES =
  'QUERY_KEY_COMPETITOR_WITH_THEMES';
export const useCompanyCompetitorWithThemes = ({
  entityTag,
  limit,
}: {
  entityTag: string;
  limit?: number;
}) =>
  useQuery<CompanyCompetitorsWithThemesResponse, WretchError>({
    queryKey: [
      QUERY_KEY_COMPANY_COMPETITOR_WITH_THEMES,
      entityTag,
      ...(limit ? [limit] : []),
    ],
    queryFn: ({ signal }) =>
      fetchCompanyCompetitorsWithThemes({ entityTag, limit, signal }),
    staleTime: getTimeTillEndOfDayMs(),
  });

export const useCompanyCompetitors = ({
  entityTag,
  limit,
}: {
  entityTag: string;
  limit?: number;
}) => {
  const {
    data: revampCompetitorsData = [],
    isFetching: isFetchingRevampCompetitorsData,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    limit,
  });

  const {
    data: competitorEntitiesData = [],
    isLoading: isLoadingCompetitorEntitiesData,
  } = useEntities({
    entityTags: revampCompetitorsData.map(s => s.company),
    enabled: !!revampCompetitorsData.length,
  });

  const competitorsData = competitorEntitiesData.map(e => ({ entity: e }));

  const isFetching =
    isFetchingRevampCompetitorsData || isLoadingCompetitorEntitiesData;

  return {
    competitorsData,
    isFetching,
  };
};
